/**
 * SOP Builder Modal (Phase 24.2)
 *
 * AI-guided conversation for generating SOPs directly from the Library page.
 * Stateless: sends full message history with each request.
 * After conversation completes, shows SOP structure preview with Accept/Regenerate.
 * On accept, persists the SOP and navigates to its detail page.
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Send,
  Loader2,
  Bot,
  User,
  FileText,
  CheckCircle2,
  RefreshCw,
  AlertCircle,
  Layers,
  ListChecks,
  Footprints,
} from 'lucide-react';
import { useSopConversation, useGenerateSop } from '../../hooks/useSOPs';

interface Message {
  role: 'ai' | 'user';
  text: string;
}

interface SopStructure {
  title: string;
  sections: Array<{
    title: string;
    description?: string;
    sectionNumber: number;
    tasks: Array<{
      title: string;
      description?: string;
      taskNumber: number;
      isSafetyCritical: boolean;
      steps: Array<{
        stepNumber: number;
        description: string;
        isSafetyCritical: boolean;
        keywords: string[];
      }>;
    }>;
  }>;
}

interface SopBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function SopBuilderModal({ isOpen, onClose, onCreated }: SopBuilderModalProps) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sopResult, setSopResult] = useState<SopStructure | null>(null);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const conversationMutation = useSopConversation();
  const generateMutation = useGenerateSop();

  // Auto-scroll to bottom
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // Start conversation on mount
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      startConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const startConversation = async () => {
    setError(null);
    setSopResult(null);
    try {
      const result = await conversationMutation.mutateAsync({
        templateCode: 'general',
        messages: [],
      });
      if (result.question) {
        setMessages([{ role: 'ai', text: result.question }]);
      }
    } catch {
      setMessages([{ role: 'ai', text: 'Sorry, I had trouble starting the conversation. Please try again.' }]);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || conversationMutation.isPending) return;

    const userMessage: Message = { role: 'user', text: input.trim() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setError(null);

    try {
      const result = await conversationMutation.mutateAsync({
        templateCode: 'general',
        messages: updatedMessages,
      });

      if (result.done && result.sopStructure) {
        const structure = result.sopStructure as unknown as SopStructure;
        setSopResult(structure);
        const sectionCount = structure.sections?.length ?? 0;
        const taskCount = structure.sections?.reduce((acc, s) => acc + s.tasks.length, 0) ?? 0;
        setMessages([...updatedMessages, {
          role: 'ai',
          text: `I've generated an SOP: "${structure.title}" with ${sectionCount} job${sectionCount !== 1 ? 's' : ''} and ${taskCount} task${taskCount !== 1 ? 's' : ''}. Review the preview below and click "Accept" to save it.`,
        }]);
      } else if (result.question) {
        setMessages([...updatedMessages, { role: 'ai', text: result.question }]);
      }
    } catch {
      setMessages([...updatedMessages, {
        role: 'ai',
        text: 'Sorry, I had trouble processing your response. Please try again.',
      }]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAccept = async () => {
    if (!sopResult) return;
    setError(null);

    try {
      const result = await generateMutation.mutateAsync(sopResult as unknown as Record<string, unknown>);
      onCreated();
      handleReset();
      onClose();
      // Navigate to the newly created SOP
      if (result.sopId) {
        navigate(`/sops/${result.sopId}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save SOP';
      setError(message);
    }
  };

  const handleRegenerate = () => {
    handleReset();
    startConversation();
  };

  const handleReset = () => {
    setMessages([]);
    setInput('');
    setSopResult(null);
    setError(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  // Compute structure summary stats
  const sectionCount = sopResult?.sections?.length ?? 0;
  const taskCount = sopResult?.sections?.reduce((acc, s) => acc + s.tasks.length, 0) ?? 0;
  const stepCount = sopResult?.sections?.reduce(
    (acc, s) => acc + s.tasks.reduce((a, t) => a + t.steps.length, 0), 0
  ) ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-guild-mint/50 flex items-center justify-center">
              <FileText size={18} className="text-guild-green" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Create SOP with AI</h2>
              <p className="text-xs text-gray-500">
                {sopResult
                  ? 'Review the generated SOP structure'
                  : 'Answer a few questions about the process you want to document'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Chat Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-[250px]">
          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
              {msg.role === 'ai' && (
                <div className="flex-shrink-0 h-7 w-7 rounded-full bg-guild-mint/50 flex items-center justify-center">
                  <Bot size={14} className="text-guild-green" />
                </div>
              )}
              <div
                className={`
                  max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed
                  ${msg.role === 'ai'
                    ? 'bg-gray-50 text-gray-800 rounded-tl-md'
                    : 'bg-guild-green text-white rounded-tr-md'
                  }
                `}
              >
                {msg.text}
              </div>
              {msg.role === 'user' && (
                <div className="flex-shrink-0 h-7 w-7 rounded-full bg-guild-green/10 flex items-center justify-center">
                  <User size={14} className="text-guild-green" />
                </div>
              )}
            </div>
          ))}

          {/* Typing indicator */}
          {conversationMutation.isPending && messages.length > 0 && messages[messages.length - 1].role === 'user' && (
            <div className="flex gap-3">
              <div className="flex-shrink-0 h-7 w-7 rounded-full bg-guild-mint/50 flex items-center justify-center">
                <Bot size={14} className="text-guild-green" />
              </div>
              <div className="bg-gray-50 rounded-2xl rounded-tl-md px-4 py-3">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* SOP Preview Card */}
        {sopResult && (
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900">{sopResult.title}</h3>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                  <CheckCircle2 size={12} />
                  AI Generated
                </span>
              </div>

              {/* Structure summary */}
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="bg-gray-50 rounded-lg p-2 flex items-center gap-2">
                  <Layers size={14} className="text-gray-400" />
                  <div>
                    <span className="text-gray-400">Jobs</span>
                    <p className="font-medium text-gray-700">{sectionCount}</p>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 flex items-center gap-2">
                  <ListChecks size={14} className="text-gray-400" />
                  <div>
                    <span className="text-gray-400">Tasks</span>
                    <p className="font-medium text-gray-700">{taskCount}</p>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2 flex items-center gap-2">
                  <Footprints size={14} className="text-gray-400" />
                  <div>
                    <span className="text-gray-400">Steps</span>
                    <p className="font-medium text-gray-700">{stepCount}</p>
                  </div>
                </div>
              </div>

              {/* Section list */}
              <div className="space-y-1.5">
                {sopResult.sections.map((section) => (
                  <div key={section.sectionNumber} className="text-xs text-gray-600 flex items-center gap-2">
                    <span className="font-mono text-gray-400 w-5 text-right">{section.sectionNumber}.</span>
                    <span className="font-medium">{section.title}</span>
                    <span className="text-gray-400">
                      ({section.tasks.length} task{section.tasks.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                <AlertCircle size={14} />
                {error}
              </div>
            )}

            {/* Accept / Regenerate actions */}
            <div className="flex gap-3 mt-3">
              <button
                onClick={handleAccept}
                disabled={generateMutation.isPending}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-guild-green px-4 py-2.5 text-sm font-semibold text-white hover:bg-guild-green/90 disabled:opacity-50 transition-all"
              >
                {generateMutation.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <CheckCircle2 size={16} />
                )}
                Accept
              </button>
              <button
                onClick={handleRegenerate}
                disabled={generateMutation.isPending}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-all"
              >
                <RefreshCw size={16} />
                Regenerate
              </button>
            </div>
          </div>
        )}

        {/* Input area (only when conversation is ongoing) */}
        {!sopResult && (
          <div className="px-6 py-4 border-t border-gray-100">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your answer..."
                disabled={conversationMutation.isPending}
                className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green/30 outline-none disabled:opacity-50"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || conversationMutation.isPending}
                className="rounded-xl bg-guild-green px-4 py-2.5 text-white hover:bg-guild-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
